import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { useDebounceFn, useMemoizedFn } from 'ahooks';
import { Divider, Form, Input, Tooltip, message } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxGalleryImg } from 'components/RevxGalleryImg';
import { RevxInputNumber } from 'components/RevxInputNumber';
import { RevxSpin } from 'components/RevxSpin';
import { CartLineItem } from 'domains/cartDomain';
import { GallerySku } from 'domains/gallerySkuDomain';
import { JobConfigViewer } from 'domains/jobconfigDomain';
import { ProjectJobAddFromEnum } from 'domains/projectDomain';
import { PDF_TEMPLATE_ENUM } from 'domains/workspaceDomain';
import NameBadge from 'features/Workspace/components/NameBadge';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import workspaceService from 'services/jobService';
import {
  EMAIL_MAXLENGTH,
  FIRST_NAME_MAXLENGTH,
  JOB_MAX_QUANTITY,
  LAST_NAME_MAXLENGTH,
  QUANTITY_DEBOUNCE_MILLISECOND,
} from 'utils/constants';
import './JobConfigDetail.less';

interface FieldError {
  name: (string | number)[];
  errors: string[];
  warnings: string[];
}
interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  jobConfigViewer?: JobConfigViewer;
  addToCArtHandler?: (e: CartLineItem) => Promise<void>;
  sku: GallerySku | undefined;
  isProject?: boolean;
}
/**
 * Renders a description's line breaks without handing its text to the HTML parser.
 *
 * The description is buyer-entered, so putting it through `dangerouslySetInnerHTML` to turn "\n"
 * into "<br/>" also executed any markup they typed. React escapes the text nodes here, so the
 * line breaks survive and the markup does not.
 */
const withLineBreaks = (text?: string) =>
  (text ?? '').split('\n').map((line, i, lines) => (
    // eslint-disable-next-line react/no-array-index-key
    <Fragment key={i}>
      {line}
      {i < lines.length - 1 && <br />}
    </Fragment>
  ));

export const JobConfigDetail = (props: Props) => {
  const { displaySize, metric } = useMeasurementUnit();
  const { jobConfigViewer, setOpen, isProject, sku } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [addToCartLoading, setAddToCartLoading] = useState<boolean>(false);
  const [quantity, setQuantity] = useState<number>(jobConfigViewer?.quantity!);
  const [jobConfig, setJobConfig] = useState<JobConfigViewer>(jobConfigViewer!);
  const [hiddenQuote, setHiddenQuote] = useState<boolean>(true);
  const jobProjectId = useAppSelector((state) => state.project.jobProjectId);
  const [addShareFormErrorList, setAddShareFormErrorList] =
    useState<FieldError[]>();
  const [form] = useForm();
  useEffect(() => {
    setQuantity(jobConfigViewer?.quantity!);
    setJobConfig(jobConfigViewer!);
  }, [jobConfigViewer]);

  const updateQuantity = useMemoizedFn(async () => {
    try {
      setLoading(true);
      const resp = await dispatch.jobConfig.summerUpdateJobConfig({
        quantity,
        id: jobConfig.id,
      });
      setJobConfig(resp);
    } catch {
    } finally {
      setLoading(false);
    }
  });

  const { run: updateMarkup, cancel: cancelUpdateMarkup } = useDebounceFn(
    async () => {
      console.log(11111);
      updateQuantity();
    },
    { wait: QUANTITY_DEBOUNCE_MILLISECOND }
  );

  const addToCartHandle = useMemoizedFn(async () => {
    try {
      setAddToCartLoading(true);
      const cartLineItem = await dispatch.jobConfig.summerAddToCart(jobConfig);
      // if (typeof addToCArtHandler === 'function') {
      //   addToCArtHandler(cartLineItem);
      // }
      if (jobProjectId) {
        await dispatch.project.jobProjectAddJob({
          projectId: jobProjectId,
          addFrom: ProjectJobAddFromEnum.SHOP_GALLERY,
          jobId: cartLineItem.jobId,
        });
        dispatch.project.reloadProjectJobList();
      }
      await dispatch.cart.getList();
      setOpen(false);
      setTimeout(() => {
        dispatch.cart.updateCartItem(cartLineItem);
      }, 100);
    } catch {
    } finally {
      setAddToCartLoading(false);
    }
  });

  const validateAddShareName = useMemo(() => {
    return ['firstName', 'lastName', 'email', 'serialNumbers'];
  }, []);

  const getFormAddShareError = async () => {
    try {
      await form.validateFields(validateAddShareName);
      setAddShareFormErrorList([]);
    } catch {
      const errorList = form.getFieldsError(validateAddShareName);
      setAddShareFormErrorList(errorList.filter((item) => item.errors.length));
    }
  };

  const addShareList = async () => {
    // setRevxSpinloading(true);
    try {
      const formData = await form.validateFields(validateAddShareName);
      const resp = await dispatch.jobConfig.summerUpdateJobConfig({
        addSharedWith: {
          ...formData,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
        },
        id: jobConfig.id,
      });
      setJobConfig(resp);

      message.success(t('jobConfig.stepViewQuote.sendShareTip'));
      form.resetFields(
        validateAddShareName.filter((item) => item !== 'serialNumbers')
      );
      setHiddenQuote(true);
      // setSharedUser(false);
      // setRevxSpinloading(false);
      // form.resetFields(validateAddShareName);
      // message.success(t('jobConfig.stepViewQuote.sendShareTip'));
      // setSharedUser(false);
      // sendShareList();
      // setRevxSpinloading(false);
    } catch {
      getFormAddShareError();
      // setRevxSpinloading(false);
    }
  };

  return (
    <>
      <RevxSpin spinning={loading}>
        <RevxFlexLayout className="jobConfig-detail" align="top">
          {/* <RevxJobImg image={jobConfigViewer?.image!} /> */}
          <div className="job-img-container">
            {/* <RevxGalleryImg src={sku?.image} /> */}
            {jobConfigViewer && (
              <RevxGalleryImg src={jobConfigViewer?.image!} />
            )}
          </div>
          <div className="jobConfig-detail-summer">
            <p style={{ marginBottom: '12px' }}>Description:</p>
            <Tooltip
              placement="bottom"
              overlayClassName="jobConfig-detail-summer-tip"
              title={<div>{withLineBreaks(jobConfigViewer?.description)}</div>}
            >
              <div className="jobConfig-detail-summer-description">
                {withLineBreaks(jobConfigViewer?.description)}
              </div>
            </Tooltip>
            <p style={{ margin: '16px 0 4px' }}>Item Specifications:</p>
            <p className="jobConfig-detail-summer-title">
              Item Type: <span>{jobConfigViewer?.jobType}</span>
            </p>
            <p className="jobConfig-detail-summer-title">
              Size: <span>{displaySize(jobConfigViewer?.finishedSizeIn, { metric: jobConfigViewer?.finishedSizeMetric })}</span>
            </p>
            <p className="jobConfig-detail-summer-title">
              Paper:{' '}
              {(
                (metric
                  ? jobConfigViewer?.paperSummariesMetric
                  : jobConfigViewer?.paperSummaries) ??
                jobConfigViewer?.paperSummaries ??
                []
              ).map((item, idx) => (
                <>
                  {idx !== 0 && <Divider type="vertical" />}
                  <span>{item}</span>
                </>
              ))}
            </p>
            <p className="jobConfig-detail-summer-title">
              Services:{' '}
              {jobConfigViewer?.svcSummaries.map((item, idx) => (
                <>
                  {idx !== 0 && <Divider type="vertical" />}
                  <span>{item}</span>
                </>
              ))}
            </p>
            <p style={{ margin: '20px 0 16px' }}>Quantity:</p>
            <RevxFlexLayout className="jobConfig-detail-summer-quantity">
              <RevxInputNumber
                min={1}
                max={JOB_MAX_QUANTITY}
                maxLength={8}
                isInteger
                value={quantity}
                disabled={sku?.qtyLocked}
                onChange={(v) => {
                  setQuantity(v);
                  cancelUpdateMarkup();
                  updateMarkup();
                }}
              />
              <RevxButton
                type="primary"
                brandGreen
                loading={addToCartLoading}
                onClick={() => {
                  addToCartHandle();
                }}
              >
                {isProject
                  ? t('gallery.skuList.addToProject')
                  : t('gallery.skuList.addToCart')}
              </RevxButton>
            </RevxFlexLayout>
            <RevxFlexLayout className="jobConfig-detail-summer-view_PDF">
              <RevxButton type="default" brandGreen>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={workspaceService.getPdfLink({
                    jobConfigId: jobConfig?.id,
                    serialNumber: 0,
                    template: PDF_TEMPLATE_ENUM.QUOTE,
                  })}
                >
                  <Trans i18nKey="jobConfig.stepViewQuote.viewPDFQuote"></Trans>
                </a>
              </RevxButton>
              <Divider type="vertical" />
              <RevxButton
                type="link"
                brandGreen
                onClick={() => {
                  setHiddenQuote((pre) => !pre);
                }}
              >
                Send Quote
              </RevxButton>
              <RevxFlexLayout
                className="jobConfig-detail-summer-shared-with"
                hidden={!jobConfig?.jobSharedWiths.length}
              >
                <Divider type="vertical" />
                <span>Shared With</span>
                {jobConfig?.jobSharedWiths?.map((item) => (
                  <RevxFlexLayout
                    justify="justify-center"
                    className="jobConfig-detail-summer-shared-name-badge"
                    key={item.email}
                  >
                    {/* <div onClick={() => deleteShareList(item)}>
                        <JobconfigViewQuoteDeleteIcon />
                      </div> */}
                    <NameBadge
                      getPopupContainer={(triggerNode) =>
                        triggerNode.parentElement || document.body
                      }
                      name={`${item.firstName} ${item.lastName}`}
                    />
                  </RevxFlexLayout>
                ))}
              </RevxFlexLayout>
            </RevxFlexLayout>
            <Form
              form={form}
              hidden={hiddenQuote}
              onChange={() => {
                getFormAddShareError();
              }}
            >
              <RevxFlexLayout
                // hidden={!sharedUser}
                justify="space-between"
                className="jobConfig-detail-summer-shared"
              >
                <div className="jobConfig-detail-summer-shared-arrow"></div>
                <RevxFlexLayout
                  className={`ant-input jobConfig-detail-summer-shared-inputContent ${
                    addShareFormErrorList &&
                    addShareFormErrorList?.filter(
                      (error) =>
                        error.name.includes('firstName') ||
                        error.name.includes('lastName')
                    ).length > 0
                      ? 'jobConfig-detail-summer-shared-inputContent-error'
                      : ''
                  }`}
                >
                  <Form.Item
                    name="firstName"
                    rules={[
                      {
                        validator: (_, value) =>
                          Boolean((value || '').trim())
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error('Please enter First Name')
                              ),
                      },
                    ]}
                  >
                    <Input
                      maxLength={FIRST_NAME_MAXLENGTH}
                      style={{ marginRight: 12 }}
                      placeholder="First Name"
                      autoComplete="new-password"
                    />
                  </Form.Item>
                  <Form.Item
                    name="lastName"
                    rules={[
                      {
                        validator: (_, value) =>
                          Boolean((value || '').trim())
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error('Please enter Last Name')
                              ),
                      },
                    ]}
                  >
                    <Input
                      maxLength={LAST_NAME_MAXLENGTH}
                      placeholder="Last Name"
                      autoComplete="new-password"
                    />
                  </Form.Item>
                </RevxFlexLayout>

                <Form.Item
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter Email',
                    },
                    {
                      type: 'email',
                      message: 'Please enter valid Email',
                    },
                  ]}
                >
                  <Input
                    maxLength={EMAIL_MAXLENGTH}
                    style={{ width: 320, height: 28 }}
                    placeholder="Email"
                    autoComplete="new-password"
                  />
                </Form.Item>

                <RevxButton
                  type="primary"
                  onClick={() => addShareList()}
                  brandOrange
                >
                  <Trans i18nKey="jobConfig.stepViewQuote.send"></Trans>
                </RevxButton>
              </RevxFlexLayout>
            </Form>
          </div>
        </RevxFlexLayout>
      </RevxSpin>
    </>
  );
};
